import CodeIcon from "@mui/icons-material/Code";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Stack, Typography } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import { LoadingButton } from "@mui/lab";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import { Panel, PanelGroup } from "@poseidon/ui/components/Panel";
import { ShowMoreDialog } from "@poseidon/ui/components/ShowMoreDialog";
import { db } from "@poseidon/firebase";
import { getDate } from "@poseidon/firebase/timestamp";
import { JsonViewer } from "@textea/json-viewer";
import { doc } from "firebase/firestore";
import { useEffect } from "react";
import { useDocumentData } from "react-firebase-hooks/firestore";
import { CreateOpenBankingConnection } from "./CreateOpenBankingConnection";
import { FetchCalculationsButton } from "./FetchCalculationsButton";
import { OpenBankingConnectionStatus } from "./OpenBankingConnectionStatus";
import { OpenBankingMonthlyAverages } from "./OpenBankingMonthlyAverages";
import { useDownloadOpenBankingReport } from "./useDownloadOpenBankingReport";
import config from "@poseidon/utils/config";
import { FileManagement } from "../Files";
import Action from "../Action/Action";
import { useSailorContext } from "../Sailors/SailorContext";
import { SailorCheckList } from "../CheckList/components/SailorCheckList";

export const OpenBanking = ({ sailorId, applicationId }) => {
  const sailor = useSailorContext();
  const { download: downloadReport, loading: downloadLoading } =
    useDownloadOpenBankingReport();

  const [openBanking, loading, error] = useDocumentData(
    doc(db, "sailorRiskProfiles", sailorId, "openBanking", "current")
  );

  const { setError } = useErrorMessage();

  useEffect(() => {
    if (error) {
      setError(error);
    }
  }, [error, setError]);

  const fileGroup = "supportingDocumentation";
  const fileCategory = "sailor";
  const { SaltCollection, fileResolvers } = config;

  return (
    <PanelGroup>
      <SailorCheckList
        title="Prerequisites"
        sailor={sailor}
        applicationId={applicationId}
        target="openBanking"
      />
      <Panel
        title="Open Banking"
        expandable
        sx={{ overflow: "auto" }}
        startCollapsed={!sailor.openBankingConsent}
        actions={
          <Stack spacing={1} direction="row">
            {openBanking?.data && (
              <ShowMoreDialog
                buttonText="View raw data"
                buttonVariant="outlined"
                title="Raw Open Banking Calculations Data"
                buttonProps={{
                  size: "small",
                  startIcon: <CodeIcon />,
                }}
              >
                <JsonViewer
                  value={openBanking?.data ?? {}}
                  displayDataTypes={false}
                />
              </ShowMoreDialog>
            )}
            {openBanking?.applicationReference && (
              <LoadingButton
                size="small"
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                loading={downloadLoading}
                onClick={() => downloadReport(sailorId)}
              >
                Download Report
              </LoadingButton>
            )}
            <FetchCalculationsButton
              sailorId={sailorId}
              disabled={!openBanking?.applicationUrl}
            />
            <CreateOpenBankingConnection
              sailorId={sailorId}
              openBankingStatus={openBanking?.openBankingStatus}
            />
          </Stack>
        }
      >
        {loading && <LinearProgress />}
        <OpenBankingConnectionStatus
          loading={loading}
          openBanking={openBanking}
        />
        {!loading && openBanking?.data && (
          <Stack spacing={2} my={2}>
            <OpenBankingMonthlyAverages data={openBanking.data?.raw} />
            <Typography variant="caption">
              Last Fetched:{" "}
              {getDate(openBanking?.lastFetched)?.toLocaleString() ?? "Unknown"}
            </Typography>
          </Stack>
        )}
      </Panel>
      <Panel title={`File Management`} expandable>
        <FileManagement
          fileCategory={fileCategory}
          resolveMetadata={fileResolvers[fileGroup]}
          sailorId={sailorId}
        />
      </Panel>
      <Panel title={`Action status`} expandable>
        <Action
          entity={SaltCollection.Sailor}
          entityId={sailorId}
          entityKey={fileGroup}
        />
      </Panel>
    </PanelGroup>
  );
};
